import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { map, Observable, startWith } from 'rxjs';
import { Big } from 'big.js';
import {
  InvoicePosition,
  NumberString,
  Summary,
  VAT_RATE_TEXTS,
  VatRate,
  VatRateDecimal,
  VatRateType,
} from '@jessie/commons';
import { NgIconWrapperComponent } from '../../../../components/icons/ng-icon-wrapper/ng-icon-wrapper.component';
import { TailwindButtonDirective } from '../../../../components/button/tailwind-button.directive';
import { TextInputComponent } from '../../../../components/input/text-input/text-input.component';
import { NumberInputComponent } from '../../../../components/input/number-input/number-input.component';
import { DropdownComponent } from '../../../../components/dropdown/dropdown.component';
import { FormatAmount } from '../../../../pipes/format-amount';

type PositionUnit = InvoicePosition['unit'];

const UNIT_TEXTS: Record<PositionUnit, string> = {
  hours: 'Stunden',
  piece: 'Stück',
  fixedPrice: 'Fixpreis',
  none: 'Ohne Einheit',
};

interface FreePositionForm {
  displayName: FormControl<string>;
  unit: FormControl<Summary>;
  quantity: FormControl<NumberString | null>;
  unitPrice: FormControl<NumberString | null>;
  totalPrice: FormControl<NumberString | null>;
  vatRateType: FormControl<Summary>;
}

export type FreePositionDialogComponentDataType = {
  position: InvoicePosition | null;
  vatRate: VatRate | null;
};

export type FreePositionDialogComponentReturnType = InvoicePosition | null;

@Component({
  selector: 'app-free-position-dialog',
  imports: [
    NgIconWrapperComponent,
    TailwindButtonDirective,
    TextInputComponent,
    NumberInputComponent,
    DropdownComponent,
    ReactiveFormsModule,
    AsyncPipe,
    FormatAmount,
  ],
  templateUrl: './free-position-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FreePositionDialogComponent implements OnInit {
  unitOptions: Summary[] = Object.entries(UNIT_TEXTS).map(([id, displayName]) => ({
    id,
    displayName,
  }));
  vatRateOptions!: Summary[];

  freePositionForm!: FormGroup<FreePositionForm>;
  totalPrice!: Observable<NumberString>;

  constructor(
    public dialogRef: DialogRef<FreePositionDialogComponentReturnType, FreePositionDialogComponent>,
    @Inject(DIALOG_DATA)
    public data: FreePositionDialogComponentDataType
  ) {}

  ngOnInit() {
    this.vatRateOptions = this.getVatRateOptions();
    const position = this.data.position;
    const unit: PositionUnit = position?.unit ?? 'piece';

    this.freePositionForm = new FormGroup<FreePositionForm>({
      displayName: new FormControl(position?.displayName ?? '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      unit: new FormControl(
        { id: unit, displayName: UNIT_TEXTS[unit] },
        { nonNullable: true, validators: [Validators.required] }
      ),
      quantity: new FormControl<NumberString | null>(position?.quantity ?? null),
      unitPrice: new FormControl<NumberString | null>(position?.unitPrice ?? null),
      totalPrice: new FormControl<NumberString | null>(position?.totalPrice ?? null),
      vatRateType: new FormControl(
        this.vatRateOptions.find(
          (option) => option.id === this.getVatRateType(position?.vatRate)
        ) ?? this.vatRateOptions[this.vatRateOptions.length - 1],
        { nonNullable: true, validators: [Validators.required] }
      ),
    });

    this.freePositionForm.controls.unit.valueChanges
      .pipe(startWith(this.freePositionForm.controls.unit.value))
      .subscribe((unit) => this.applyUnitValidators(unit.id as PositionUnit));

    this.totalPrice = this.freePositionForm.valueChanges.pipe(
      startWith(null),
      map(() => this.calculateTotalPrice())
    );
  }

  get isQuantityBased(): boolean {
    const unit = this.freePositionForm.controls.unit.value.id as PositionUnit;
    return unit === 'hours' || unit === 'piece';
  }

  savePosition() {
    this.freePositionForm.markAllAsTouched();
    if (!this.freePositionForm.valid) {
      return;
    }
    const value = this.freePositionForm.getRawValue();
    const unit = value.unit.id as PositionUnit;
    const isQuantityBased = this.isQuantityBased;
    this.dialogRef.close({
      positionId: this.data.position?.positionId ?? crypto.randomUUID(),
      displayName: value.displayName.trim(),
      quantity: isQuantityBased ? value.quantity : null,
      unit,
      unitPrice: isQuantityBased ? value.unitPrice : null,
      totalPrice: this.calculateTotalPrice(),
      vatRate: this.getVatRateDecimal(value.vatRateType.id as VatRateType),
      works: this.data.position?.works ?? [],
      source: 'manual',
    });
  }

  cancelDialog(): void {
    this.dialogRef.close(null);
  }

  private applyUnitValidators(unit: PositionUnit) {
    const { quantity, unitPrice, totalPrice } = this.freePositionForm.controls;
    if (unit === 'hours' || unit === 'piece') {
      quantity.setValidators([Validators.required]);
      unitPrice.setValidators([Validators.required]);
      totalPrice.clearValidators();
    } else {
      quantity.clearValidators();
      unitPrice.clearValidators();
      totalPrice.setValidators([Validators.required]);
    }
    quantity.updateValueAndValidity({ emitEvent: false });
    unitPrice.updateValueAndValidity({ emitEvent: false });
    totalPrice.updateValueAndValidity({ emitEvent: false });
  }

  private calculateTotalPrice(): NumberString {
    const value = this.freePositionForm.getRawValue();
    try {
      if (this.isQuantityBased) {
        return new Big(value.quantity ?? 0).mul(new Big(value.unitPrice ?? 0)).toFixed(2);
      }
      return new Big(value.totalPrice ?? 0).toFixed(2);
    } catch {
      return '0.00';
    }
  }

  private getVatRateOptions(): Summary[] {
    const vatRate = this.data.vatRate;
    const rateTypes: Exclude<VatRateType, 'none'>[] = vatRate
      ? ['standardRate', 'reducedRate', 'specialRate']
      : [];
    return [
      ...rateTypes.map((type) => ({
        id: type,
        displayName: `${VAT_RATE_TEXTS[type]} (${new Big(vatRate![type]).mul(100).toString()}%)`,
      })),
      { id: 'none', displayName: VAT_RATE_TEXTS.none },
    ];
  }

  private getVatRateDecimal(vatRateType: VatRateType): VatRateDecimal {
    if (vatRateType === 'none' || !this.data.vatRate) {
      return '0.00';
    }
    return this.data.vatRate[vatRateType];
  }

  private getVatRateType(vatRateDecimal: VatRateDecimal | undefined): VatRateType {
    const vatRate = this.data.vatRate;
    if (!vatRateDecimal || !vatRate) {
      return vatRate ? 'standardRate' : 'none';
    }
    return (
      (['standardRate', 'reducedRate', 'specialRate'] as const).find(
        (type) => vatRate[type] === vatRateDecimal
      ) ?? 'none'
    );
  }
}
